import { FiBook } from 'react-icons/fi';

export const dailyLifeVocabData = {
  courseId: 'daily-life-vocab',
  courseType: 'vocabulary',
  title: 'Daily Life Vocabulary',
  icon: FiBook,
  overview: {
    impact: 'Describe routines and everyday situations confidently',
    focus: 'Words for home, food, transport, shopping, weather',
    uses: 'Speaking Part 1, Listening Sections 1-2',
    duration: '20 minutes'
  },
  sections: [
    {
      title: 'Home & Routine',
      content: `<p>Essential vocabulary for describing daily life:</p>`,
      points: [
        'Morning routine: wake up, get dressed, brush teeth, have breakfast, commute',
        'Household: kitchen, bedroom, living room, bathroom, balcony, garden',
        'Chores: do the laundry, vacuum, wash dishes, take out the rubbish, tidy up'
      ]
    },
    {
      title: 'Food & Shopping',
      content: `<p>Common vocabulary for these frequent Speaking Part 1 topics:</p>`,
      points: [
        'Meals: breakfast, lunch, dinner, snack, brunch',
        'Cooking: boil, fry, bake, grill, steam, roast',
        'Shopping: grocery store, supermarket, market, online shopping, bargain, receipt'
      ]
    },
    {
      title: 'Weather & Transport',
      content: `<p>Topics that come up regularly in IELTS:</p>`,
      points: [
        'Weather: sunny, cloudy, rainy, windy, humid, freezing, mild, boiling',
        'Transport: commute, public transport, rush hour, traffic jam, fare, pedestrian'
      ]
    }
  ],
  exercises: [
    {
      title: 'Complete the Sentence',
      type: 'select',
      instructions: 'Choose the best word.',
      questions: [
        {
          text: 'I usually ___ at 6:30 every morning.',
          options: [
            'wake up',
            'stand up',
            'get on'
          ],
          answer: 'wake up',
          explanation: '\'Wake up\' means to stop sleeping.'
        },
        {
          text: 'She takes the ___ to work every day.',
          options: [
            'commute',
            'bus',
            'traffic'
          ],
          answer: 'bus',
          explanation: '\'Bus\' is the transport. \'Commute\' is the journey itself.'
        },
        {
          text: 'The weather is very ___ today — about 35°C.',
          options: [
            'boiling',
            'freezing',
            'mild'
          ],
          answer: 'boiling',
          explanation: '35°C is very hot → \'boiling\' (informal).'
        }
      ]
    },
    {
      title: 'Match Word to Category',
      type: 'select',
      instructions: 'Choose the category that each word belongs to.',
      questions: [
        {
          text: 'Which category does \'vacuum\' belong to?',
          options: ['Home & Chores', 'Transport', 'Shopping', 'Weather'],
          answer: 'Home & Chores',
          explanation: '\'Vacuum\' (to clean with a vacuum cleaner) is a household chore.'
        },
        {
          text: 'Which category does \'fare\' belong to?',
          options: ['Transport', 'Food', 'Shopping', 'Home & Chores'],
          answer: 'Transport',
          explanation: '\'Fare\' is the price you pay for a bus, train, or taxi journey.'
        },
        {
          text: 'Which category does \'bargain\' belong to?',
          options: ['Shopping', 'Weather', 'Transport', 'Home & Chores'],
          answer: 'Shopping',
          explanation: '\'Bargain\' means something bought at a lower price than usual.'
        },
        {
          text: 'Which category does \'humid\' belong to?',
          options: ['Weather', 'Home & Chores', 'Food', 'Transport'],
          answer: 'Weather',
          explanation: '\'Humid\' describes weather with a high level of moisture in the air.'
        }
      ]
    },
    {
      title: 'Complete Daily Routine Sentences',
      type: 'fill-in',
      instructions: 'Type the correct word or phrase to complete each sentence about daily routines.',
      questions: [
        {
          text: 'Every morning I ___ my teeth before having breakfast.',
          answer: 'brush',
          explanation: 'The collocation is \'brush your teeth\' — a standard part of a morning routine.'
        },
        {
          text: 'She has to ___ the laundry every weekend.',
          answer: 'do',
          explanation: 'The correct collocation is \'do the laundry\', not \'make the laundry\'.'
        },
        {
          text: 'I usually ___ to work by train. It takes about 40 minutes.',
          answer: 'commute',
          explanation: '\'Commute\' means to travel regularly between home and work.'
        },
        {
          text: 'Can you help me ___ out the rubbish? The bin is full.',
          answer: 'take',
          explanation: 'The phrase is \'take out the rubbish\' (British) or \'take out the trash\' (American).'
        }
      ]
    },
    {
      title: 'Describe Your Routine',
      type: 'writing',
      instructions: 'Write 1-2 sentences answering each question. Use daily life vocabulary from the lesson.',
      questions: [
        {
          text: 'What do you usually do in the morning before you leave home?',
          answer: 'wake up, get dressed, have breakfast',
          explanation: 'Example: \'I usually wake up at seven, brush my teeth, and have a quick breakfast before I commute to work.\' Use sequencing words like \'first\', \'then\', \'after that\'.'
        },
        {
          text: 'How do you usually get to work or school?',
          answer: 'commute, public transport, bus',
          explanation: 'Example: \'I commute by bus during rush hour, which takes about 30 minutes.\' Include transport vocabulary and time expressions.'
        },
        {
          text: 'What household chores do you do regularly?',
          answer: 'do the laundry, wash dishes, tidy up',
          explanation: 'Example: \'I do the laundry on Saturdays and wash the dishes every evening after dinner.\' Use correct collocations with \'do\' for chores.'
        }
      ]
    }
  ],
  tips: [
    'Group vocabulary by topic for easier memorization',
    'Practice describing YOUR daily routine using new words',
    'Learn collocations: \'do the laundry\' not \'make the laundry\'',
    'These topics are the most common in Speaking Part 1'
  ],
  dailyPractice: [
    'Practice daily life vocabulary concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'Daily Life Vocabulary: Home, routine, food, shopping, weather, transport words for IELTS Speaking Part 1.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...dailyLifeVocabData,
      title: dailyLifeVocabData.title + ' (Foundations)',
      overview: { ...dailyLifeVocabData.overview, duration: '15-20 minutes' },
      sections: dailyLifeVocabData.sections.slice(0, Math.max(2, Math.ceil(dailyLifeVocabData.sections.length / 2))),
      exercises: dailyLifeVocabData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...dailyLifeVocabData,
      title: dailyLifeVocabData.title + ' (Advanced)',
      overview: { ...dailyLifeVocabData.overview, duration: '30-35 minutes' },
      sections: dailyLifeVocabData.sections,
      exercises: dailyLifeVocabData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(dailyLifeVocabData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return dailyLifeVocabData;
}
